import React, { useState, useEffect, useCallback } from 'react';
import timeTrackingService from '../services/timeTrackingService';
import TimeTrackingNav from '../components/TimeTrackingNav';

const TYPE_LABELS  = { pto: 'PTO', sick: 'Sick', unpaid: 'Unpaid', other: 'Other' };
const STATUS_BADGE = {
  pending:  { cls: 'text-bg-warning text-dark', label: 'Pending' },
  approved: { cls: 'text-bg-success',            label: 'Approved' },
  denied:   { cls: 'text-bg-danger',             label: 'Denied' },
};

function ReviewModal({ request, action, onClose, onDone }) {
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async () => {
    setSaving(true); setErr('');
    try {
      if (action === 'approve') await timeTrackingService.approvePtoRequest(request.id, adminNote);
      else await timeTrackingService.denyPtoRequest(request.id, adminNote);
      onDone();
    } catch (e) {
      setErr(e?.response?.data?.error || 'Action failed.');
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-sm">
        <div className="modal-content">
          <div className="modal-header">
            <h6 className="modal-title">{action === 'approve' ? 'Approve' : 'Deny'} Request</h6>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="mb-2" style={{ fontSize: 13 }}>
              <strong>{request.Employee?.firstName} {request.Employee?.lastName}</strong>
              {' — '}{TYPE_LABELS[request.type]} · {request.hours} h
              <div className="text-muted">{request.startDate === request.endDate ? request.startDate : `${request.startDate} – ${request.endDate}`}</div>
            </div>
            {err && <div className="alert alert-danger py-1" style={{ fontSize: 12 }}>{err}</div>}
            <label className="form-label" style={{ fontSize: 12 }}>Admin Note (optional)</label>
            <textarea className="form-control form-control-sm" rows={2} value={adminNote} onChange={e => setAdminNote(e.target.value)} />
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
            <button
              className={`btn btn-sm ${action === 'approve' ? 'btn-success' : 'btn-danger'}`}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
              {action === 'approve' ? 'Approve' : 'Deny'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PtoRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [review, setReview] = useState(null); // { request, action }

  const load = useCallback(() => {
    setLoading(true);
    timeTrackingService.getPtoRequests({ status: statusFilter || undefined })
      .then(setRequests).catch(() => setRequests([])).finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="container mt-4">
      <TimeTrackingNav />
      <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
        <h5 className="mb-0">Time-Off Requests</h5>
        <select className="form-select form-select-sm" style={{ width: 130 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="denied">Denied</option>
          <option value="">All</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" /></div>
      ) : requests.length === 0 ? (
        <div className="text-muted text-center py-5">No {statusFilter} requests.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ fontSize: 13 }}>
            <thead className="table-light">
              <tr>
                <th>Employee</th>
                <th>Type</th>
                <th>Dates</th>
                <th>Hours</th>
                <th>Note</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id}>
                  <td className="fw-semibold">{r.Employee?.firstName} {r.Employee?.lastName}</td>
                  <td>{TYPE_LABELS[r.type]}</td>
                  <td className="text-nowrap">{r.startDate === r.endDate ? r.startDate : `${r.startDate} – ${r.endDate}`}</td>
                  <td>{r.hours} h</td>
                  <td className="text-muted">{r.note || '—'}</td>
                  <td><span className={`badge ${STATUS_BADGE[r.status].cls}`} style={{ fontSize: 11 }}>{STATUS_BADGE[r.status].label}</span></td>
                  <td>
                    {r.status === 'pending' && (
                      <div className="d-flex gap-1">
                        <button className="btn btn-success btn-sm py-0 px-2" onClick={() => setReview({ request: r, action: 'approve' })}>✓</button>
                        <button className="btn btn-danger  btn-sm py-0 px-2" onClick={() => setReview({ request: r, action: 'deny' })}>✗</button>
                      </div>
                    )}
                    {r.status !== 'pending' && r.adminNote && (
                      <span className="text-muted" style={{ fontSize: 11 }} title={r.adminNote}>Note ✓</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {review && (
        <ReviewModal
          request={review.request}
          action={review.action}
          onClose={() => setReview(null)}
          onDone={() => { setReview(null); load(); }}
        />
      )}
    </div>
  );
}
